import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { isLoading } from 'holocron';
import { Button } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import {
  TAB_TO_ELEMENT, TAB_TO_ICON, TAB_TO_MODULE_NAME,
} from '../../constants/linesOfBusinessConstants';
import getTabSelected from '../../selectors/tabSelector';
import styles from './SearchWidgetNavigation.scss';
import LobBadge from './LobBadge/LobBadge';

/* istanbul ignore next -- coverage in __tests__/playwright/navigation.spec.ts */
const MosaicSearchWidgetNavigation = ({
  displayedLOBs, onTabSelected,
}) => {
  const { formatMessage } = useIntl();
  const navRef = useRef(null);
  const selectedTab = useSelector(getTabSelected);
  const tabCount = displayedLOBs.length;
  const tabPosition = displayedLOBs.indexOf(
    displayedLOBs.find((lob) => lob === selectedTab)
  );
  const isSelectedTabLoading = useSelector(isLoading(TAB_TO_MODULE_NAME[selectedTab]));

  const handleDirectionButtonClick = (direction) => {
    const positionOfNextTab = (tabPosition + direction + tabCount) % tabCount;
    const nextTab = displayedLOBs[positionOfNextTab];
    onTabSelected(nextTab);
    navRef?.current?.querySelector(`button[id="${nextTab}-tab"]`)?.focus();
  };

  const navButton = (lob) => {
    const isSelected = selectedTab === lob;
    return (
      <Button
        variant="secondary"
        className="pad-0 margin-1-tb margin-1-l-xs-down margin-0-r-xs-down margin-1-lr-sm-up margin-2-lr-md-up margin-1-lr-lg-up"
        style={isSelected ? { backgroundColor: '#e0eef9' } : {}}
        id={`${lob}-tab`}
        role="tab"
        onFocus={() => onTabSelected(lob)}
        onKeyDownCapture={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onTabSelected(lob); }
          if (e.key === 'ArrowLeft') { handleDirectionButtonClick(-1); }
          if (e.key === 'ArrowRight') { handleDirectionButtonClick(1); }
        }}
        onClick={() => onTabSelected(lob)}
        aria-selected={isSelected}
        tabIndex={isSelected ? 0 : -1}
        aria-controls={`${lob}-body`}
        reactlytics={{ element: TAB_TO_ELEMENT[lob] || lob }}
      >
        <div
          className={clsx(
            'flex flex-column-md-down flex-align-items-center flex-justify-center body-1 pad',
            isSelected && 'font-weight-bold',
            styles.navButton
          )}
        >
          {TAB_TO_ICON(isSelected, 'pad-1-r-lg-up')[lob]}
          {formatMessage({ id: lob })}
        </div>
      </Button>
    );
  };

  const navButtonXsScreen = (lob) => {
    const isSelected = selectedTab === lob;
    const isButtonLoading = !!(isSelected && isSelectedTabLoading);

    return (
      <Button
        variant="secondary"
        className="pad-0 margin-1-tb margin-1-l-xs-down margin-0-r-xs-down margin-1-lr-sm-up margin-2-lr-md-up margin-1-lr-lg-up"
        id={`xs-${lob}-tab`}
        isLoading={isButtonLoading}
        onClick={() => onTabSelected(lob, true)}
        reactlytics={{ element: TAB_TO_ELEMENT[lob] || lob }}
      >
        <div className={clsx('flex flex-column-md-down flex-align-items-center flex-justify-center body-1 pad', styles.navButton)}>
          <LobBadge lob={lob} />
          {TAB_TO_ICON(false, 'pad-1-r-lg-up')[lob]}
          {formatMessage({ id: lob })}
        </div>
      </Button>
    );
  };

  return (
    <>
      <nav
        ref={navRef}
        id="search-widget-navigation"
        // TODO: add translations for aria-label once we have them to give navigation context
        aria-label={formatMessage({ id: 'aria.search_widget' })}
      >
        <div
          id="search-widget-tabs"
          role="tablist"
          className={clsx('hidden-xs-down dls-white-bg pad-1-tb flex-justify-center', styles.mosaicNav)}
        >
          {displayedLOBs.map((lob) => navButton(lob))}
        </div>
      </nav>
      <div
        id="xs-search-widget-navigation"
        className={clsx(['hidden-sm-up dls-white-bg pad-1-tb flex-justify-center', styles.mosaicNav, {
          [styles.mosaicNavLayout4Lobs]: displayedLOBs.length === 4,
          [styles.mosaicNavLayout5Lobs]: displayedLOBs.length === 5,
          [styles.mosaicNavLayoutOver5Lobs]: displayedLOBs.length >= 6,
        }])}
      >
        {displayedLOBs.map((lob) => navButtonXsScreen(lob))}
      </div>
    </>
  );
};

MosaicSearchWidgetNavigation.propTypes = {
  displayedLOBs: PropTypes.arrayOf(
    PropTypes.string.isRequired
  ).isRequired,
  onTabSelected: PropTypes.func.isRequired,
};

export default MosaicSearchWidgetNavigation;
